import React, { useEffect, useState } from 'react'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import { Accordion, Badge, Button, Col, Form, Modal, Row } from 'react-bootstrap'
import { locales, t } from '../locale'
import { Client } from '../react-app-env'
import { CompanyForm, PersonForm } from '../types/stammdaten.type'
import { ensureEnoughCredit } from '../helpers/credit.helper'
import { checkCredit } from '../service/subscription'

interface SignModalProps {
  show: boolean
  onClose: () => void
  onAccept: (payload: any) => void
  firmename: string
  documentName: string
  client: Client | CompanyForm | PersonForm | null
  stammdaten?: boolean
  stammdatenType?: string
}

const initialRecipient = { name: '', email: '', role: 'needs_to_sign', sequence: 1 }

const roleBadges: Record<string, string> = {
  needs_to_sign: 'success',
  needs_to_view: 'primary',
}

const roleLabels: Record<string, string> = {
  needs_to_sign: 'needsToSign',
  needs_to_view: 'viewOnly',
}

// `ready` means the backend price is known; confirmation additionally requires credit.
type QesPriceState = 'idle' | 'loading' | 'ready' | 'pending' | 'error'

const KSignModal: React.FC<SignModalProps> = ({
  show,
  onClose,
  onAccept,
  firmename,
  documentName,
  client,
  stammdaten,
  stammdatenType,
}) => {
  const [title, setTitle] = useState('')
  const [signatureStandardId, setSignatureStandardId] = useState(1)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')
  const [enforceOrder, setEnforceOrder] = useState(true)
  const [recipients, setRecipients] = useState([{ ...initialRecipient }])
  const [errors, setErrors] = useState<string[]>([])
  const [signOrderLabel, setSignOrderLabel] = useState('disableOrder')
  const [language, setLanguage] = useState('de')
  // QES price (points) from the backend, so the displayed amount matches the deduction.
  const [qesPriceState, setQesPriceState] = useState<QesPriceState>('idle')
  const [qesPrice, setQesPrice] = useState<number | null>(null)
  const [qesFirstUse, setQesFirstUse] = useState(false)
  const [qesInsufficientCredit, setQesInsufficientCredit] = useState(false)

  const resetForm = () => {
    setTitle('')
    setSignatureStandardId(1)
    setEmailSubject('')
    setEmailMessage('')
    setEnforceOrder(true)
    setRecipients([{ ...initialRecipient }])
    setErrors([])
    setQesPriceState('idle')
    setQesPrice(null)
    setQesFirstUse(false)
    setQesInsufficientCredit(false)
  }

  // Drops any displayed QES amount the instant its inputs change, so a stale price is
  // never shown or confirmed while the new lookup is in flight.
  const invalidateQesPrice = () => {
    if (Number(signatureStandardId) === 3) {
      setQesPriceState('loading')
      setQesPrice(null)
      setQesFirstUse(false)
      setQesInsufficientCredit(false)
    }
  }

  useEffect(() => {
    if (show) {
      setTitle(documentName)
      const isClient = client !== null && 'firstName' in client
      if (isClient) {
        setRecipients([
          {
            ...initialRecipient,
            name: `${(client as Client).firstName ?? ''} ${client!.name ?? ''}`.trim(),
            email: (client as Client).email ?? '',
          },
        ])
      }
    } else {
      resetForm()
    }
  }, [show, documentName, client])

  useEffect(() => {
    if (!show || Number(signatureStandardId) !== 3) {
      setQesPriceState('idle')
      setQesPrice(null)
      setQesFirstUse(false)
      setQesInsufficientCredit(false)
      return
    }

    setQesPriceState('loading')
    setQesPrice(null)
    setQesFirstUse(false)
    setQesInsufficientCredit(false)

    let active = true

    checkCredit('ds-sign-qes', recipients.length)
      .then(result => {
        if (!active) return

        if (result?.qesUnavailable) {
          setQesPriceState('error')
          return
        }

        if (result?.qesPending) {
          setQesPriceState('pending')
          return
        }

        if (typeof result?.cost === 'number') {
          setQesPrice(result.cost)
          setQesFirstUse(!!result.firstUse)
          setQesInsufficientCredit(result.allowed === false)
          setQesPriceState('ready')
          return
        }

        setQesPriceState('error')
      })
      .catch(() => {
        if (active) setQesPriceState('error')
      })

    return () => {
      active = false
    }
  }, [show, signatureStandardId, recipients.length])

  const validate = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const errs: string[] = []

    if (!title.trim()) errs.push('Document title is required.')
    if (!emailSubject.trim()) errs.push('Email subject is required.')
    if (recipients.length === 0) errs.push('At least one recipient is required.')

    recipients.forEach((r, idx) => {
      if (!r.name.trim()) errs.push(t('recipient') + ` ${idx + 1}: ` + t('nameRequired.'))
      if (!r.email.trim()) errs.push(t('recipient') + ` ${idx + 1} :` + t('emailRequired'))
      else if (!emailRegex.test(r.email))
        errs.push(t('recipient') + ` ${idx + 1}: ` + t('emailInvalid'))
      if (!r.role.trim()) errs.push(`Recipient ${idx + 1}: role is required.`)
    })

    if (enforceOrder) {
      const sequences = recipients.map(r => r.sequence)
      const hasDuplicates = sequences.length !== new Set(sequences).size
      if (hasDuplicates) errs.push('recipientsSequenceNumber')
    }

    setErrors(errs)
    return errs.length === 0
  }

  const handleAddRecipient = () => {
    invalidateQesPrice()
    setRecipients([...recipients, { ...initialRecipient, sequence: recipients.length + 1 }])
  }

  const handleRemoveRecipient = (index: number) => {
    invalidateQesPrice()
    const updated = [...recipients]
    updated.splice(index, 1)
    setRecipients(updated.map((r, i) => ({ ...r, sequence: i + 1 })))
  }

  const handleRecipientChange = (index: number, key: string, value: string | number) => {
    const updated = [...recipients]
    if (key === 'sequence' && !enforceOrder) return
    if (key === 'sequence') {
      const newSeq = Number(value)
      const conflictIndex = updated.findIndex((r, i) => i !== index && r.sequence === newSeq)
      if (conflictIndex !== -1) {
        updated[conflictIndex].sequence = updated[index].sequence
      }
    }
    // @ts-ignore
    updated[index][key] = value
    setRecipients(updated)
  }

  const handleAccept = async () => {
    if (!validate()) return

    // Per-signer cost for the chosen standard (mirrors backend countCredit).
    const signService =
      Number(signatureStandardId) === 2
        ? 'ds-sign-aes'
        : Number(signatureStandardId) === 3
          ? 'ds-sign-qes'
          : 'ds-sign-ses'
    if (!(await ensureEnoughCredit(signService, recipients.length))) return

    const payload = {
      meta: {
        clientId: localStorage.getItem('clientId'),
        firmename: firmename,
        documentName: documentName,
        stammdaten: stammdaten,
        stammdatenType: stammdatenType,
      },
      signingData: {
        title,
        signature_standard_id: signatureStandardId,
        email_subject: emailSubject,
        email_message: emailMessage,
        sequence: enforceOrder ? 1 : false,
        recipients: enforceOrder
          ? recipients.map(r => ({ ...r }))
          : recipients.map(r => ({ ...r, sequence: undefined })),
        preferred_language: language === 'en-GB' ? 'en' : language,
      },
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.REACT_APP_API_KEY || ''}`,
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        onClose()
      }

      const result = await response.json()
      console.log('Uspešno poslato:', result)

      onClose()
    } catch (err) {
      console.error(err)
      setErrors([...(errors || []), 'Greška prilikom slanja podataka.'])
      throw new Error('Greška pri slanju podataka')
    }
  }

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const updated = [...recipients]
    const [moved] = updated.splice(result.source.index, 1)
    updated.splice(result.destination.index, 0, moved)

    setRecipients(updated.map((r, i) => ({ ...r, sequence: i + 1 })))
  }

  return (
    <Modal show={show} onHide={onClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('documentInformation')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errors.length > 0 && (
          <div className='alert alert-danger'>
            {errors.map((e, idx) => (
              <div key={idx}>{t(e)}</div>
            ))}
          </div>
        )}

        <Form>
          <Form.Group className='mb-3'>
            <Form.Label>{t('documentTitle')}</Form.Label>
            <Form.Control
              readOnly
              type='text'
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>{t('emailSubject')}</Form.Label>
            <Form.Control
              type='text'
              value={emailSubject}
              onChange={e => setEmailSubject(e.target.value)}
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>{t('emailMessage')}</Form.Label>
            <Form.Control
              as='textarea'
              rows={2}
              value={emailMessage}
              onChange={e => setEmailMessage(e.target.value)}
            />
          </Form.Group>

          {enforceOrder ? (
            <div className='alert alert-warning mb-3'>{t('signingOrderOn')}</div>
          ) : (
            <div className='alert alert-info mb-3'>{t('signingOrderOff')}</div>
          )}
          <Row className='mb-3'>
            <Col>
              <Form.Label>{t('signatureStandard')}</Form.Label>
              <Form.Select
                value={signatureStandardId}
                onChange={e => {
                  setQesPriceState('idle')
                  setQesPrice(null)
                  setQesFirstUse(false)
                  setSignatureStandardId(Number(e.target.value))
                }}
              >
                <option value={1}>SES</option>
                <option value={2}>AES</option>
                <option value={3}>QES</option>
              </Form.Select>
            </Col>
            <Col className='d-flex align-items-end'>
              <Form.Check
                type='switch'
                id='enforce-order'
                label={t(signOrderLabel)}
                checked={enforceOrder}
                onChange={e => {
                  signOrderLabel === 'disableOrder'
                    ? setSignOrderLabel('enableOrder')
                    : setSignOrderLabel('disableOrder')
                  const on = e.target.checked
                  setEnforceOrder(on)
                  setRecipients(prev =>
                    on ? prev.map((r, i) => ({ ...r, sequence: i + 1 })) : prev
                  )
                }}
              />
            </Col>
          </Row>
          {Number(signatureStandardId) === 3 && (
            <Row className='mb-3'>
              <Col>
                {qesPriceState === 'ready' ? (
                  <div
                    className={`alert py-2 mb-0 ${
                      qesInsufficientCredit ? 'alert-danger' : 'alert-info'
                    }`}
                  >
                    <div className='fw-medium'>
                      {t('qesPrice')}: {qesPrice !== null ? qesPrice.toFixed(2) : '—'} {t('points')}
                    </div>
                    {qesInsufficientCredit ? (
                      <div className='small mt-1'>{t('notEnoughCredit')}</div>
                    ) : (
                      qesFirstUse && (
                        <div className='small mt-1'>{t('qesFirstUseSurchargeNote')}</div>
                      )
                    )}
                  </div>
                ) : qesPriceState === 'pending' ? (
                  <div className='alert alert-warning py-2 mb-0 fw-medium'>
                    {t('qesFirstUsePending')}
                  </div>
                ) : qesPriceState === 'error' ? (
                  <div className='alert alert-danger py-2 mb-0 fw-medium'>
                    {t('qesPriceUnavailable')}
                  </div>
                ) : (
                  <div className='alert alert-info py-2 mb-0'>{t('qesPriceLoading')}</div>
                )}
              </Col>
            </Row>
          )}
          <Row className='mb-3'>
            <Col xs={6}>
              <Form.Label>{t('language')}</Form.Label>
              <Form.Select value={language} onChange={e => setLanguage(e.target.value)}>
                {Object.entries(locales).map(([locale, language]) => (
                  <option value={locale}>{language}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          <hr />
          <h5 className='mb-3'>{t('recipients')}</h5>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='recipients' isDropDisabled={!enforceOrder}>
              {provided => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  <Accordion>
                    {recipients.map((recipient, index) => (
                      <Draggable
                        key={index.toString()}
                        draggableId={index.toString()}
                        index={index}
                        isDragDisabled={!enforceOrder}
                      >
                        {provided => (
                          <Accordion.Item
                            eventKey={index.toString()}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Accordion.Header>
                              #{index + 1} - {recipient.name || t('unnamed')}
                              <Badge className='ms-2' bg={roleBadges[recipient.role]}>
                                {t(roleLabels[recipient.role])}
                              </Badge>
                              {enforceOrder && (
                                <Badge className='ms-2' bg='secondary'>
                                  {t('order')} {recipient.sequence}
                                </Badge>
                              )}
                            </Accordion.Header>
                            <Accordion.Body>
                              <Row className='mb-2'>
                                <Col>
                                  <Form.Label>{t('nameSign')}</Form.Label>
                                  <Form.Control
                                    type='text'
                                    value={recipient.name}
                                    onChange={e =>
                                      handleRecipientChange(index, 'name', e.target.value)
                                    }
                                  />
                                </Col>
                                <Col>
                                  <Form.Label>{t('Email')}</Form.Label>
                                  <Form.Control
                                    type='email'
                                    value={recipient.email}
                                    onChange={e =>
                                      handleRecipientChange(index, 'email', e.target.value)
                                    }
                                  />
                                </Col>
                              </Row>
                              <Row className='align-items-end'>
                                <Col>
                                  <Form.Label>{t('role')}</Form.Label>
                                  <Form.Select
                                    value={recipient.role}
                                    onChange={e =>
                                      handleRecipientChange(index, 'role', e.target.value)
                                    }
                                  >
                                    <option value='needs_to_sign'>{t('needsToSign')}</option>
                                    <option value='needs_to_view'>{t('viewOnly')}</option>
                                    {/* <option value="receives_copy">Receives Copy</option> */}
                                  </Form.Select>
                                </Col>
                                <Col>
                                  <Form.Label>{t('sequence')}</Form.Label>
                                  <Form.Control
                                    type='number'
                                    value={recipient.sequence ?? ''}
                                    onChange={e =>
                                      handleRecipientChange(
                                        index,
                                        'sequence',
                                        Number(e.target.value)
                                      )
                                    }
                                    disabled={!enforceOrder} // ← disable ako je paralelno
                                    placeholder={
                                      enforceOrder ? '1, 2, 3...' : 'N/A in parallel mode'
                                    }
                                  />
                                </Col>
                                <Col xs='auto'>
                                  {recipients.length > 1 && (
                                    <Button
                                      variant='danger'
                                      onClick={() => handleRemoveRecipient(index)}
                                      className='mt-3'
                                    >
                                      {t('delete')}
                                    </Button>
                                  )}
                                </Col>
                              </Row>
                            </Accordion.Body>
                          </Accordion.Item>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Accordion>
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <Button variant='outline-primary' className='mt-3' onClick={handleAddRecipient}>
            {t('addAnotherRecipient')}
          </Button>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='warning' onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          variant='success'
          onClick={handleAccept}
          disabled={
            Number(signatureStandardId) === 3 &&
            (qesPriceState !== 'ready' || qesInsufficientCredit)
          }
        >
          {t('confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default KSignModal
